import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, MapPin, Phone, ScrollText } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import FAQAccordion from "@/components/FAQAccordion";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { TRACKS } from "@/lib/tracks";
import { SITE_URL, SITE_NAME } from "@/lib/seo-constants";
import { breadcrumbListSchema, faqPageSchema } from "@/lib/jsonld";
import { LOCATIONS, LOCATION_BY_SLUG } from "@/data/locations";
import { hasLocalService } from "@/data/location-services";

// City landing page: /locations/:city. Every section is driven by
// data/locations.js so each city carries its own municipal rules, areas and
// FAQs — not a find-and-replace of one template (Google treats those as
// doorway pages). Rules there are sourced from the municipality's own site.

const cityServiceSchema = (loc) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Exterior contracting in ${loc.name}`,
    serviceType: "Exterior and general contracting",
    description: loc.metaDescription,
    url: `${SITE_URL}/locations/${loc.slug}`,
    provider: {
        "@type": "GeneralContractor",
        "@id": `${SITE_URL}/#localbusiness`,
        name: SITE_NAME,
        url: SITE_URL,
    },
    areaServed: {
        "@type": "City",
        name: loc.name,
        containedInPlace: { "@type": "AdministrativeArea", name: loc.region },
    },
});

const Hero = ({ loc }) => {
    const { phone, phoneHref } = usePhoneNumber();
    return (
        <section
            className="relative w-full overflow-hidden bg-brand-dark text-white"
            data-testid="location-hero"
        >
            <Container className="relative z-10 py-16 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <div className="lg:col-span-7">
                        <nav
                            className="text-xs text-white/50 mb-6"
                            aria-label="Breadcrumb"
                        >
                            <Link to="/service-area" className="hover:text-brand-cyan">
                                Service Area
                            </Link>
                            <span className="mx-2">/</span>
                            <span className="text-white/80">{loc.name}</span>
                        </nav>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide text-white">
                            <span className="block font-sans text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold leading-normal mb-4">
                                {loc.name} Exterior Contractor · Reformity Inc.
                            </span>
                            Fencing, Decks &amp; Interlocking in{" "}
                            <span className="text-brand-coral">{loc.name}.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light italic leading-relaxed">
                            {loc.heroSubhead}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <BrandButton asChild variant="outline" tone="onDark" size="lg">
                                <a href={phoneHref} data-testid="location-cta-call">
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {phone}
                                </a>
                            </BrandButton>
                        </div>
                        <p className="mt-8 text-xs sm:text-sm text-white/60">
                            Licensed &amp; insured · WSIB covered · $5M liability · Free on-site
                            quotes in {loc.name}
                        </p>
                    </div>
                    <div className="lg:col-span-5" data-testid="location-quickquote">
                        <QuickQuoteForm
                            track="residential"
                            defaultCity={loc.name}
                            source={`location_${loc.slug}`}
                            title={`Get a ${loc.name} quote.`}
                        />
                    </div>
                </div>
            </Container>
        </section>
    );
};

const Intro = ({ loc }) => (
    <Section bg="white" data-testid="location-intro">
        <Container size="narrow">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                Working in {loc.name}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none mb-6">
                {loc.introHeading}
            </h2>
            <div className="space-y-4 text-base md:text-lg text-brand-dark/80 leading-relaxed">
                {loc.intro.map((para, i) => (
                    <p key={i}>{para}</p>
                ))}
            </div>
        </Container>
    </Section>
);

const Services = ({ loc }) => {
    const residential = TRACKS.residential.services;
    return (
        <Section dark data-testid="location-services">
            <Container>
                <div className="max-w-2xl mb-10 md:mb-12">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        Services in {loc.name}
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                        What we build here.
                    </h2>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                    {residential.map((s) => (
                        <li key={s.path} className="bg-brand-dark">
                            <Link
                                // City × service page when we have one (e.g.
                                // /locations/brampton/fencing), else the service page.
                                to={
                                    hasLocalService(loc.slug, s.path.split("/").pop())
                                        ? `/locations/${loc.slug}/${s.path.split("/").pop()}`
                                        : s.path
                                }
                                className="group flex items-center justify-between gap-3 p-6 h-full hover:bg-[#333] transition-colors"
                                data-testid={`location-service-${s.path.split("/").pop()}`}
                            >
                                <span className="font-semibold text-white group-hover:text-brand-cyan transition-colors">
                                    {s.label}
                                    <span className="block text-xs font-normal text-white/50 mt-1">
                                        in {loc.name}
                                    </span>
                                </span>
                                <ArrowRight
                                    className="h-5 w-5 shrink-0 text-white/40 group-hover:text-brand-cyan group-hover:translate-x-1 transition-all"
                                    strokeWidth={2.25}
                                />
                            </Link>
                        </li>
                    ))}
                </ul>
                <p className="mt-8 text-sm text-white/70">
                    Property manager or facility owner in {loc.name}? See{" "}
                    <Link to="/commercial" className="text-brand-cyan font-semibold hover:underline">
                        commercial
                    </Link>{" "}
                    and{" "}
                    <Link to="/industrial" className="text-brand-cyan font-semibold hover:underline">
                        industrial
                    </Link>{" "}
                    contracting. COIs &amp; WSIB clearance on request.
                </p>
            </Container>
        </Section>
    );
};

const LocalRules = ({ loc }) => {
    if (!loc.rules || loc.rules.length === 0) return null;
    return (
        <Section data-testid="location-rules">
            <Container>
                <div className="max-w-2xl mb-10 md:mb-12">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        {loc.municipality} Rules
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                        Know the bylaws before you build.
                    </h2>
                    <p className="mt-4 text-base text-brand-dark/70 leading-relaxed">
                        We check these on every {loc.name} quote visit. Bylaws change and
                        lots differ (corner lots, easements, ravine areas), so treat this as a
                        starting point and confirm with the {loc.municipality} for your
                        address.
                    </p>
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {loc.rules.map((r) => (
                        <li
                            key={r.title}
                            className="bg-white border border-brand-dark/10 p-6 md:p-7"
                        >
                            <div className="flex items-start gap-3">
                                <ScrollText
                                    className="h-5 w-5 mt-0.5 shrink-0 text-brand-cyan"
                                    strokeWidth={2.25}
                                />
                                <div>
                                    <h3 className="font-sans font-semibold text-lg text-brand-dark leading-snug">
                                        {r.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-brand-dark/75 leading-relaxed">
                                        {r.body}
                                    </p>
                                    {r.source && r.source.startsWith("/") && (
                                        <Link
                                            to={r.source}
                                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-cyan hover:text-brand-coral transition-colors"
                                        >
                                            {r.sourceLabel}
                                            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
                                        </Link>
                                    )}
                                    {r.source && !r.source.startsWith("/") && (
                                        <a
                                            href={r.source}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-cyan hover:text-brand-coral transition-colors"
                                        >
                                            {r.sourceLabel || "Official source"}
                                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </Container>
        </Section>
    );
};

const Areas = ({ loc }) => (
    <Section bg="white" className="py-14 md:py-16" data-testid="location-areas">
        <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        Neighbourhoods
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-none">
                        Across {loc.name}.
                    </h2>
                </div>
                <ul className="lg:col-span-8 flex flex-wrap gap-2">
                    {loc.neighbourhoods.map((n) => (
                        <li
                            key={n}
                            className="inline-flex items-center gap-1.5 bg-brand-offwhite border border-brand-dark/10 px-3 py-2 text-sm text-brand-dark"
                        >
                            <MapPin className="h-3.5 w-3.5 text-brand-cyan" strokeWidth={2.5} />
                            {n}
                        </li>
                    ))}
                </ul>
            </div>
        </Container>
    </Section>
);

const Guides = ({ loc }) => {
    if (!loc.guides || loc.guides.length === 0) return null;
    return (
        <Section data-testid="location-guides">
            <Container>
                <div className="mb-8 md:mb-10 max-w-2xl">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        Local Guides
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                        Costs, permits &amp; bylaws.
                    </h2>
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-dark/10">
                    {loc.guides.map((g) => (
                        <li key={g.path} className="bg-white">
                            <Link
                                to={g.path}
                                className="group flex items-start justify-between gap-4 p-6 h-full hover:bg-brand-offwhite transition-colors"
                            >
                                <span className="text-base font-semibold text-brand-dark leading-snug group-hover:text-brand-cyan transition-colors">
                                    {g.title}
                                </span>
                                <ArrowRight
                                    className="h-5 w-5 mt-0.5 shrink-0 text-brand-dark/40 group-hover:text-brand-cyan group-hover:translate-x-1 transition-all"
                                    strokeWidth={2.25}
                                />
                            </Link>
                        </li>
                    ))}
                </ul>
            </Container>
        </Section>
    );
};

const Faq = ({ loc }) => (
    <Section bg="white" data-testid="location-faq">
        <Container size="narrow">
            <div className="mb-8 md:mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    {loc.name} FAQ
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    Quick answers.
                </h2>
            </div>
            <FAQAccordion items={loc.faqs} testidPrefix={`location-faq-${loc.slug}`} />
        </Container>
    </Section>
);

const Nearby = ({ loc }) => {
    const others = LOCATIONS.filter((l) => l.slug !== loc.slug);
    return (
        <Section dark className="py-14 md:py-16" data-testid="location-nearby">
            <Container>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-4">
                    Also serving
                </p>
                <ul className="flex flex-wrap gap-2">
                    {others.map((l) => (
                        <li key={l.slug}>
                            <Link
                                to={`/locations/${l.slug}`}
                                className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/80 hover:bg-brand-coral hover:border-brand-coral hover:text-white transition-colors"
                            >
                                <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
                                {l.name}
                            </Link>
                        </li>
                    ))}
                    <li>
                        <Link
                            to="/service-area"
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-brand-cyan hover:underline"
                        >
                            Full service area
                            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </Link>
                    </li>
                </ul>
            </Container>
        </Section>
    );
};

export default function Location() {
    const { city } = useParams();
    const loc = LOCATION_BY_SLUG[city];
    if (!loc) return <Navigate to="/service-area" replace />;

    const path = `/locations/${loc.slug}`;
    const jsonLd = [
        cityServiceSchema(loc),
        faqPageSchema(loc.faqs),
        breadcrumbListSchema([
            { name: "Service Area", path: "/service-area" },
            { name: loc.name, path },
        ]),
    ];
    return (
        <>
            <Seo
                title={loc.metaTitle}
                description={loc.metaDescription}
                path={path}
                jsonLd={jsonLd}
            />
            <Hero loc={loc} />
            <Intro loc={loc} />
            <Services loc={loc} />
            <LocalRules loc={loc} />
            <Areas loc={loc} />
            <Guides loc={loc} />
            <Faq loc={loc} />
            <Nearby loc={loc} />
        </>
    );
}
